import { useTranslate } from '@im/react-localization';
import { Pagination } from '@im/ui-lib/Pagination';
import Box from '@mui/material/Box';
import React, { FC } from 'react';
import { AcopDetails, AcopSearchResponse } from '../../hooks/acopList';
import { Tag } from '@im/ui-lib/Tag';
import { useDateTimeFormatter } from 'src/hooks/dateTimeFormatter';
import Tooltip from '@mui/material/Tooltip';
import CustomTable from 'src/components/TableComponent/TableComponent';
import { ColumnDef } from 'src/components/TableComponent/types';

interface AcopResultTableProps {
  acopListData?: AcopSearchResponse;
  bundleMode?: boolean;
  isAcopListLoading: boolean;
  isOpenOrdersOnly: boolean | undefined;
  pageSetUp: { page: number; pageSize: number };
  selectedRows: string[];
  setPageSetUp: React.Dispatch<
    React.SetStateAction<AcopResultTableProps['pageSetUp']>
  >;
  setSelectedRows: React.Dispatch<
    React.SetStateAction<AcopResultTableProps['selectedRows']>
  >;
}

const AcopResultTable: FC<AcopResultTableProps> = ({
  acopListData,
  bundleMode,
  isAcopListLoading,
  isOpenOrdersOnly,
  pageSetUp,
  selectedRows,
  setPageSetUp,
  setSelectedRows,
}) => {
  const translate = useTranslate();
  const formatDateTime = useDateTimeFormatter();

  const columnNames = [
    {
      field: 'vendorName',
      headerName: translate('AcopMaintenance.ColumnVendor'),
    },
    {
      field: 'programName',
      headerName: translate('AcopMaintenance.ColumnProgram'),
      width: 250,
    },
    {
      field: 'bidNumber',
      headerName: translate('AcopMaintenance.ColumnBidNumber'),
      width: 250,
    },
    {
      field: 'acopTrackingNumber',
      headerName: translate('AcopMaintenance.ColumnAcopTracking'),
    },
    {
      field: 'orderStatus',
      headerName: translate('AcopMaintenance.ColumnOrderStatus'),
    },
    {
      field: 'discountTrackingId',
      headerName: translate('AcopMaintenance.ColumnDiscountTrackingId'),
    },
    {
      field: 'uniqueDiscountTrackingId',
      headerName: translate('AcopMaintenance.ColumnDiscountTransactionId'),
    },
    { field: 'bcn', headerName: translate('AcopMaintenance.ColumnBCN') },
    { field: 'sku', headerName: translate('AcopMaintenance.ColumnSku') },
    {
      field: 'orderNumber',
      headerName: translate('AcopMaintenance.ColumnOrderNumber'),
    },
    {
      field: 'lineNumber',
      headerName: translate('AcopMaintenance.ColumnLineNumber'),
    },
    {
      field: 'acopStatus',
      headerName: translate('AcopMaintenance.ColumnStatus'),
    },
    {
      field: 'quantity',
      headerName: translate('AcopMaintenance.ColumnQuantity'),
    },
    {
      field: 'bundleId',
      headerName: translate('AcopMaintenance.ColumnBundleId'),
    },
    {
      field: 'responseStatus',
      headerName: translate('AcopMaintenance.ColumnResponseStatus'),
    },
    {
      field: 'responseStatusReason',
      headerName: translate('AcopMaintenance.ColumnResponseStatusReason'),
      width: 250,
    },
    {
      field: 'createTimestamp',
      headerName: translate('AcopMaintenance.ColumnCreatedOn'),
      width: 200,
    },
    {
      field: 'orderDate',
      headerName: translate('AcopMaintenance.ColumnOrderedOn'),
      width: 200,
    },
    {
      field: 'createdBy',
      headerName: translate('AcopMaintenance.ColumnUser'),
      width: 250,
    },
  ];

  const columns: ColumnDef<AcopDetails>[] = columnNames.map((col) => ({
    field: col.field as keyof AcopDetails,
    headerName: col.headerName,
    renderCell: (item) => {
      switch (col.field) {
        case 'acopStatus':
          switch (item.value) {
            case 'Applied':
              return <Tag label={item.value} variant="success" />;
            case 'Expired':
              return <Tag label={item.value} variant="error" />;
            default:
              return <Tag label={item.value} variant="warning" />;
          }
        case 'createTimestamp':
        case 'updateTimestamp':
        case 'orderDate':
          if (item.value) {
            const date = formatDateTime(String(item.value), true);
            return (
              <Tooltip title={date}>
                <div>{date}</div>
              </Tooltip>
            );
          }
          break;
        default:
          return (
            <Tooltip title={item.value ?? ''}>
              <div>{item.value}</div>
            </Tooltip>
          );
      }
    },
    sortable: false,
    width: col.width ?? 150,
  }));

  const requestPagination = () => (
    <Pagination
      data-testid="acop-maintanance-table-pagination"
      onPageChange={(page) => setPageSetUp({ ...pageSetUp, page })}
      onRowsPerPageChange={(pageSize) => setPageSetUp({ page: 1, pageSize })}
      page={pageSetUp.page}
      rowsPerPage={pageSetUp.pageSize}
      totalCount={acopListData?.responsePreamble.totalCount ?? 0}
    />
  );
  return (
    <Box>
      <CustomTable
        columns={columns}
        customMessage={
          acopListData?.data.length === 0 && isOpenOrdersOnly
            ? translate('Common.NoOpenRecord')
            : undefined
        }
        data={acopListData?.data ?? []}
        getRowId={(row) => row.id}
        isLoading={isAcopListLoading}
        onSelectionChange={(selected) => {
          setSelectedRows(selected);
        }}
        paginationComponent={requestPagination()}
        pinnedColumns={[
          'vendorName',
          'programName',
          'bidNumber',
          'acopTrackingNumber',
        ]}
        selectable={!bundleMode}
        selection={selectedRows}
      />
    </Box>
  );
};

export default AcopResultTable;
